import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import { getTicketNumber } from '~utils/tickets/ticket-utils';
import { downloadFile } from '~utils/utilFunctions';

import { FlexColumn, FlexRow } from '../styled';
import { TicketDrawerBusyReason } from './constants';
import ImageUploader from './ImageUploader';
import { SmallIconButton } from './styled';
import TicketImage from './TicketImage';

interface TicketAttachmentsProps {
  busyReason?: TicketDrawerBusyReason;
  isTicketImageEditable?: boolean;
  load: Load;
  onDeleteTicketImage?: () => Promise<void>;
  onTicketImageStateChange: (isManipulatinImage: boolean) => void;
  refreshTicket: (ticket: Ticket, isNewTicket?: boolean) => void;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function TicketAttachments({
  busyReason,
  isTicketImageEditable,
  load,
  onDeleteTicketImage,
  onTicketImageStateChange,
  refreshTicket,
  sx,
  ticket,
}: TicketAttachmentsProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const hasTicketNumber = Boolean(getTicketNumber(ticket));

  const handleDeleteTicketImage = useCallback(async () => {
    if (!onDeleteTicketImage) {
      return;
    }

    try {
      setIsDeleting(true);
      onTicketImageStateChange(true);
      await onDeleteTicketImage?.();
    } finally {
      setIsDeleting(false);
      onTicketImageStateChange(false);
    }
  }, [onDeleteTicketImage, onTicketImageStateChange]);

  return (
    <Box sx={[{ p: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {(ticket?.imageUrl || hasTicketNumber) && (
        <FlexRow
          sx={{
            alignItems: 'center',
            gap: 2,
            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <TicketNumber ticket={ticket} />

          {ticket?.imageUrl && (
            <FlexRow sx={{ alignItems: 'center', ml: 'auto', gap: 1 }}>
              {onDeleteTicketImage && isTicketImageEditable && (
                <>
                  <SmallIconButton
                    disabled={Boolean(isDeleting || busyReason)}
                    onClick={handleDeleteTicketImage}
                    sx={{
                      ...(isDeleting
                        ? { pointerEvents: 'auto !important', cursor: 'not-allowed' }
                        : {}),
                    }}
                  >
                    {isDeleting ? (
                      <FlexRow>
                        <CircularProgress color="secondary" size={15} />
                      </FlexRow>
                    ) : (
                      <DeleteOutlineOutlined
                        sx={(theme) => ({ color: theme.brandV2.colors.treadRed })}
                      />
                    )}
                  </SmallIconButton>

                  <Divider orientation="vertical" flexItem />
                </>
              )}

              <SmallButton
                color="inherit"
                disabled={Boolean(isDeleting || busyReason)}
                onClick={() => downloadFile(ticket.imageUrl, ticket.imageUrl, true)}
                startIcon={<FileDownloadOutlined />}
                variant="outlined"
                sx={(theme) => ({
                  bgcolor: 'background.paper',
                  border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                })}
              >
                {t('approvals.files.download_image_cta')}
              </SmallButton>
            </FlexRow>
          )}
        </FlexRow>
      )}

      <FlexColumn
        sx={{
          height: ticket?.imageUrl ? '700px' : 'auto',
          maxHeight: ticket?.imageUrl ? '700px' : 'none',
          width: '100%',
        }}
      >
        <TicketImageManager
          isImageEditable={isTicketImageEditable}
          load={load}
          onTicketImageStateChange={onTicketImageStateChange}
          refreshTicket={refreshTicket}
          ticket={ticket}
        />
      </FlexColumn>
    </Box>
  );
}

interface TicketNumberProps {
  ticket?: Ticket;
}

function TicketNumber({ ticket }: TicketNumberProps) {
  const ticketNumber = getTicketNumber(ticket);

  if (!ticketNumber) {
    return null;
  }

  return (
    <Typography
      sx={{
        fontSize: '16px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray2,
          fontSize: 'inherit',
        })}
      >{`${t('form_fields.ticket')} #`}</Typography>

      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: 'inherit',
          fontWeight: 600,
          ml: 0.5,
        })}
      >
        {ticketNumber}
      </Typography>
    </Typography>
  );
}

interface TicketImageManagerProps {
  isImageEditable?: boolean;
  load: Load;
  onTicketImageStateChange: (isManipulatinImage: boolean) => void;
  refreshTicket: (ticket: Ticket, isNewTicket?: boolean) => void;
  ticket?: Ticket;
}

function TicketImageManager({
  isImageEditable,
  load,
  onTicketImageStateChange,
  refreshTicket,
  ticket,
}: TicketImageManagerProps) {
  const hasImage = ticket?.imageUrl;

  if (!hasImage) {
    return (
      <ImageUploader
        isDisabled={!isImageEditable}
        load={load}
        onTicketImageStateChange={onTicketImageStateChange}
        refreshTicket={refreshTicket}
        ticket={ticket}
      />
    );
  }

  return (
    <Box
      sx={(theme) => ({
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 2,
        display: 'flex',
        maxHeight: '100%',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      })}
    >
      <TicketImage
        imageUrl={ticket.imageUrl}
        imageMimeType={ticket.imageMimeType ?? ''}
        isDisabled={!isImageEditable}
        onTicketImageStateChange={onTicketImageStateChange}
        refreshTicket={refreshTicket}
        ticketId={ticket.id}
      />
    </Box>
  );
}
